'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepForm } from '../../../../form-controls/MultistepForm/MultistepForm';
import { MyAccountSheet } from '../../../MyAccountSheet/MyAccountSheet';
import { type AccountReviewReminderDTO, ACCOUNT_REVIEW_REMINDER_HEADERS, Interval } from '../../hooks/useAccountReviewReminderData';
import { ConfirmationStep } from './components/ConfirmationStep';
import { SelectIntervalStep } from './components/SelectIntervalStep';

type AccountReviewReminderSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentInterval?: Interval;
};

const INTERVAL_OPTIONS = [
  { value: Interval.SIX_MONTHS, labelKey: 'options.P6M' },
  { value: Interval.THREE_MONTHS, labelKey: 'options.P3M' },
  { value: Interval.ONE_MONTH, labelKey: 'options.P1M' },
] as const;

const DEFAULT_INTERVAL = Interval.SIX_MONTHS;

const formSchema = z.object({
  interval: z.nativeEnum(Interval, { message: 'Please select a reminder interval' }),
});

type AccountReviewReminderFormData = z.infer<typeof formSchema>;

type AccountReviewReminderMutation = ReturnType<typeof useApiMutation<AccountReviewReminderDTO, AccountReviewReminderDTO>>;

function AccountReviewReminderSheet({ open, onOpenChange, currentInterval }: AccountReviewReminderSheetProps) {
  const t = useTranslations('my_account.settings.account_review_reminder_sheet');
  const queryClient = useQueryClient();
  const debug = createDebug({ isEnabled: false });

  const form = useForm<AccountReviewReminderFormData>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    values: {
      interval: currentInterval ?? DEFAULT_INTERVAL,
    },
  });

  const mutation = useApiMutation<AccountReviewReminderDTO, AccountReviewReminderDTO>(API_ROUTES.MY_ACCOUNT.ACCOUNT_REVIEW_REMINDER.PUT, 'PUT', {
    requestOptions: {
      credentials: true,
      headers: ACCOUNT_REVIEW_REMINDER_HEADERS,
    },
    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['api', API_ROUTES.MY_ACCOUNT.ACCOUNT_REVIEW_REMINDER.GET] });
      },
      onError: (error: Error) => {
        debug.forceError('Error updating account review reminder:', error);
      },
    },
  });

  const steps = [
    {
      id: 'select-interval',
      component: <SelectIntervalStep mutation={mutation} />,
      schema: formSchema,
    },
    {
      id: 'confirmation',
      component: <ConfirmationStep />,
      schema: z.object({}),
    },
  ];

  const handleClose = () => onOpenChange(false);

  if (!open) return null;

  return (
    <MyAccountSheet trigger={<div />} title={t('title')} open={open} onOpenChange={onOpenChange} data-track-container="Account Review Reminder">
      <FormProvider {...form}>
        <MultistepForm steps={steps} showProgressBar={false} showNavigation={false} onComplete={handleClose} className="h-full" />
      </FormProvider>
    </MyAccountSheet>
  );
}

export { AccountReviewReminderSheet, INTERVAL_OPTIONS, type AccountReviewReminderMutation, type AccountReviewReminderSheetProps };
